import { useEffect, useRef, useState, useCallback, useMemo } from "react"
import { Map, View } from "ol"
import TileLayer from "ol/layer/Tile"
import OSM from "ol/source/OSM"
import { fromLonLat, toLonLat } from "ol/proj"
import VectorLayer from "ol/layer/Vector"
import VectorSource from "ol/source/Vector"
import Feature from "ol/Feature"
import LineString from "ol/geom/LineString"
import Style from "ol/style/Style"
import Stroke from "ol/style/Stroke"
import Point from "ol/geom/Point"
import CircleStyle from "ol/style/Circle"
import Fill from "ol/style/Fill"
import Text from "ol/style/Text"
import Overlay from "ol/Overlay"
import { getDistance } from "ol/sphere"
import CoordinateCompletionDialog from '../sectores/CoordinateCompletionDialog';

import "../../css/mapa.css"
import apiService from "../../shared/services/ApiService"
import SectorForm from "../sectores/SectorForm"
import socketService from '../../shared/services/socket'
import Cookies from 'js-cookie';

// Constantes
const LATITUD_CENTRO = 18.4861
const LONGITUD_CENTRO = -69.9312
const ZOOM = 15

// Colores de la aplicación
const COLORS = {
    primary: "#3498db",
    primaryHover: "#2980b9",
    danger: "#e74c3c",
    dangerHover: "#c0392b",
    success: "#2ecc71",
    successHover: "#27ae60",
    bgLight: "#f8f9fa",
    accent1: "#008f45",
    accent2: "#0057A6",
    pending: "#e74c3c",
    inProcess: "#f39c12",
    completed: "#2ecc71",
    white: "#ffffff",
    black: "#333333",
    border: "#e0e0e0",
    shadow: "rgba(0, 0, 0, 0.1)",
    userLocation: "#3498db",
    newMarker: "#9b59b6",
    customMarker: "#e67e22",
    completedPoint: "#2ecc71",
    pendingPoint: "#e74c3c",
    userColors: ["#e74c3c", "#3498db", "#2ecc71", "#f39c12", "#9b59b6", "#1abc9c", "#e67e22", "#95a5a6"]
}

// Helper para obtener color por estado
const getStatusColor = (status) => {
    const statusLower = status?.toLowerCase()
    switch (statusLower) {
        case "pendiente":
            return COLORS.pending
        case "proceso":
            return COLORS.inProcess
        case "listo":
            return COLORS.completed
        default:
            return COLORS.completed
    }
}

// Helper para obtener color por usuario
const getUserColor = (userId, userLocations) => {
    const userIds = Object.keys(userLocations)
    const index = userIds.indexOf(userId)
    return COLORS.userColors[index % COLORS.userColors.length]
}

const Mapa = () => {
    const mapaRef = useRef()
    const mapaInstancia = useRef()
    const searchRef = useRef(null)
    const clickListenerRef = useRef(null)
    const ultimaCapaRef = useRef(null)
    const userLocationsLayerRef = useRef(null)
    const sectorProgressLayerRef = useRef(null)
    const [mostrarLeyenda, setMostrarLeyenda] = useState(false);

    // ⚡ ESTADOS PARA COMPLETACIÓN DE COORDENADAS
    const [showCoordinateDialog, setShowCoordinateDialog] = useState(false);
    const [selectedCoordinate, setSelectedCoordinate] = useState(null);

    // ⚡ ESTADO PARA DETECTAR EL ROL DEL USUARIO
    const [currentUserRole] = useState(Cookies.get('role') || '')
    const isSupervisor = currentUserRole === 'Supervisor'
    const isAdmin = currentUserRole === 'Administrador'

    const [sectors, setSectors] = useState([])
    const [posicionActual, setPosicionActual] = useState(null)
    const [userLocationMarker, setUserLocationMarker] = useState(null)
    const [busqueda, setBusqueda] = useState("")
    const [sugerencias, setSugerencias] = useState([])
    const [cargando, setCargando] = useState(true)
    const [sectorSeleccionado, setSectorSeleccionado] = useState(null)
    const [brigades, setBrigades] = useState([])
    const [circunscripciones, setCircunscripciones] = useState([])
    const [infoOverlay, setInfoOverlay] = useState(null)
    const [customMarker, setCustomMarker] = useState(null)
    const [showSectorForm, setShowSectorForm] = useState(false)
    const [newSectorCoordinates, setNewSectorCoordinates] = useState(null)
    const [showSectorsList, setShowSectorsList] = useState(true)
    const [editingSector, setEditingSector] = useState(null)

    // Estados para el modo de edición
    const [modoEdicion, setModoEdicion] = useState(false)
    const [coordenadasTemporal, setCoordenadasTemporal] = useState([])
    const [marcadoresLayer, setMarcadoresLayer] = useState(null)
    const [lineaLayer, setLineaLayer] = useState(null)

    // ⚡ ESTADOS PARA SOCKET Y TRACKING
    const [isSocketConnected, setIsSocketConnected] = useState(false)
    const [isTracking, setIsTracking] = useState(false)
    const [userLocations, setUserLocations] = useState({})
    const [, setOnlineUsers] = useState([])
    const [showUsersPanel, setShowUsersPanel] = useState(false)

    // ⚡ ESTADOS PARA TRACKING DE SECTOR
    const [activeSectorTracking, setActiveSectorTracking] = useState(null)
    const [sectorProgress, setSectorProgress] = useState({})
    const [showCompletionDialog, setShowCompletionDialog] = useState(false)
    const [completedSectorData, setCompletedSectorData] = useState(null)

    // ============================================
    // ⚡ FUNCIONES PARA COMPLETACIÓN DE COORDENADAS
    // ============================================

 
    const drawSectorProgress = useCallback((progressData) => {
        if (!activeSectorTracking) {
            console.log('⚠️ No hay tracking activo');
            return;
        }

        // REMOVER CAPAS ANTERIORES CORRECTAMENTE
        if (sectorProgressLayerRef.current) {
            if (sectorProgressLayerRef.current.linesLayer) {
                mapaInstancia.current.removeLayer(sectorProgressLayerRef.current.linesLayer);
            }
            if (sectorProgressLayerRef.current.pointsLayer) {
                mapaInstancia.current.removeLayer(sectorProgressLayerRef.current.pointsLayer);
            }
        }

        const coordinates = activeSectorTracking.coordinates;
        console.log('🗺️ Dibujando progreso para sector:', coordinates);
        const completedCount = progressData?.completedPoints || 0;

        const lineFeatures = [];
        const pointFeatures = [];

        // 1. DIBUJAR LÍNEAS DE PROGRESO
        if (completedCount > 0) {
            const completedCoords = coordinates
                .slice(0, completedCount + 1)
                .map(coord => fromLonLat([coord[0], coord[1]]));

            if (completedCoords.length > 1) {
                const completedLine = new Feature({
                    geometry: new LineString(completedCoords),
                });

                completedLine.setStyle(
                    new Style({
                        stroke: new Stroke({
                            color: COLORS.completedPoint,
                            width: 5,
                            lineCap: 'round',
                            lineJoin: 'round',
                        }),
                    })
                );

                lineFeatures.push(completedLine);
                console.log('✅ Línea verde creada');
            }
        }

        if (completedCount < coordinates.length - 1) {
            const pendingCoords = coordinates
                .slice(completedCount)
                .map(coord => fromLonLat([coord[0], coord[1]]));

            if (pendingCoords.length > 1) {
                const pendingLine = new Feature({
                    geometry: new LineString(pendingCoords),
                });

                pendingLine.setStyle(
                    new Style({
                        stroke: new Stroke({
                            color: COLORS.pendingPoint,
                            width: 5,
                            lineCap: 'round',
                            lineJoin: 'round',
                            lineDash: [10, 5],
                        }),
                    })
                );

                lineFeatures.push(pendingLine);
            }
        }

        // 2. AGREGAR INDICADORES DE PULSO
        if (completedCount > 0 && completedCount < coordinates.length) {
            const currentCoord = coordinates[completedCount];

            const outerPulse = new Feature({
                geometry: new Point(fromLonLat([currentCoord[0], currentCoord[1]])),
            });

            outerPulse.setStyle(
                new Style({
                    image: new CircleStyle({
                        radius: 25,
                        fill: new Fill({
                            color: 'rgba(46, 204, 113, 0.15)'
                        }),
                        stroke: new Stroke({
                            color: COLORS.completedPoint,
                            width: 2,
                            lineDash: [8, 4]
                        }),
                    }),
                })
            );

            pointFeatures.push(outerPulse);

            const innerPulse = new Feature({
                geometry: new Point(fromLonLat([currentCoord[0], currentCoord[1]])),
            });

            innerPulse.setStyle(
                new Style({
                    image: new CircleStyle({
                        radius: 18,
                        fill: new Fill({
                            color: 'rgba(46, 204, 113, 0.25)'
                        }),
                        stroke: new Stroke({
                            color: COLORS.completedPoint,
                            width: 2,
                        }),
                    }),
                })
            );

            pointFeatures.push(innerPulse);
        }

        // 3. DIBUJAR PUNTOS NUMERADOS CLICKEABLES
        coordinates.forEach((coord, index) => {
            const isCompleted = index < completedCount;
            const isCurrent = index === completedCount;

            const point = new Feature({
                geometry: new Point(fromLonLat([coord[0], coord[1]])),
            });

            point.set('coordinateIndex', index);
            point.set('isClickable', true);
            point.set('isCompleted', isCompleted);
            point.set('isCurrent', isCurrent);

            const radius = isCurrent ? 14 : 10;
            const strokeWidth = isCurrent ? 4 : 3;

            point.setStyle(
                new Style({
                    image: new CircleStyle({
                        radius: radius,
                        fill: new Fill({
                            color: isCompleted ? COLORS.completedPoint : COLORS.pendingPoint
                        }),
                        stroke: new Stroke({
                            color: COLORS.white,
                            width: strokeWidth
                        }),
                    }),
                    text: new Text({
                        text: (index + 1).toString(),
                        fill: new Fill({ color: COLORS.white }),
                        font: 'bold 13px Arial',
                        offsetY: 0,
                    })
                })
            );

            pointFeatures.push(point);
        });

        // 4. CREAR Y AGREGAR CAPAS
        const linesLayer = new VectorLayer({
            source: new VectorSource({ features: lineFeatures }),
            zIndex: 1001,
        });

        const pointsLayer = new VectorLayer({
            source: new VectorSource({ features: pointFeatures }),
            zIndex: 1002,
        });

        mapaInstancia.current.addLayer(linesLayer);
        mapaInstancia.current.addLayer(pointsLayer);

     

        sectorProgressLayerRef.current = {
            linesLayer,
            pointsLayer
        };

        // 5. CENTRAR MAPA EN EL PUNTO ACTUAL
        if (completedCount < coordinates.length) {
            const currentCoord = coordinates[completedCount];
            const coords = fromLonLat([currentCoord[0], currentCoord[1]]);
            mapaInstancia.current.getView().animate({
                center: coords,
                duration: 500,
            });
        }
        

    }, [activeSectorTracking]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleCoordinateClick = useCallback((coordinateIndex) => {
        if (!activeSectorTracking) return;

        const progressData = sectorProgress[activeSectorTracking.sectorId];
        const completedPoints = progressData?.completedPoints || 0;

        if (coordinateIndex < completedPoints) {
            const confirmView = window.confirm(
                'Este punto ya ha sido completado. ¿Deseas ver o editar sus detalles?'
            );

            if (!confirmView) return;
        }

        setSelectedCoordinate({
            index: coordinateIndex,
            sectorId: activeSectorTracking.sectorId,
            sectorName: activeSectorTracking.sectorName,
            totalCoordinates: activeSectorTracking.totalPoints
        });
        setShowCoordinateDialog(true);
    }, [activeSectorTracking,sectorProgress]);

    const handleCoordinateComplete = useCallback(async (data) => {
        if (activeSectorTracking && data.data) {
            const newProgress = {
                completedPoints: data.data.progress.completedPoints,
                totalPoints: data.data.progress.totalPoints,
                progress: data.data.progress.percentage
            };

            console.log('📊 Nuevo progreso:', newProgress);

            setSectorProgress(prev => {
                const updated = {
                    ...prev,
                    [activeSectorTracking.sectorId]: newProgress
                };
              
                return updated;
            });

            setTimeout(() => {
             
                drawSectorProgress(newProgress);
            }, 100);

            setShowCoordinateDialog(false);
            setSelectedCoordinate(null);

            if (newProgress.completedPoints === newProgress.totalPoints) {
                const duration = Math.round((Date.now() - new Date(activeSectorTracking.startTime).getTime()) / 1000 / 60);

                setCompletedSectorData({
                    sectorId: activeSectorTracking.sectorId,
                    sectorName: activeSectorTracking.sectorName,
                    duration: duration,
                    completedPoints: newProgress.completedPoints,
                    totalPoints: newProgress.totalPoints,
                    message: `¡Has completado todos los ${newProgress.totalPoints} puntos del sector "${activeSectorTracking.sectorName}"!`
                });
                setShowCompletionDialog(true);
            }
        }
    }, [activeSectorTracking, drawSectorProgress]);

    // Agrupar sectores por fecha
    const sectoresPorFecha = () => {
        const grupos = {}
        sectors.forEach(sector => {
            if (sector.scheduledDate) {
                const fecha = new Date(sector.scheduledDate).toLocaleDateString('es-ES', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                })
                if (!grupos[fecha]) {
                    grupos[fecha] = []
                }
                grupos[fecha].push(sector)
            }
        })
        return grupos
    }

    const sectorFormData = useMemo(() => {
        if (editingSector) {
            return editingSector;
        }
        if (newSectorCoordinates) {
            return { coordinates: newSectorCoordinates };
        }
        return null;
    }, [editingSector, newSectorCoordinates]);

    const handleToggleTracking = useCallback(() => {
        if (isTracking) {
            socketService.stopLocationTracking();
            setIsTracking(false);
         
        } else {
            const started = socketService.startLocationTracking({
                updateInterval: 10000,
                enableHighAccuracy: true
            });

            if (started) {
                setIsTracking(true);
             

                if (navigator.geolocation) {
                    navigator.geolocation.getCurrentPosition((position) => {
                        const coordenadas = [position.coords.longitude, position.coords.latitude];
                        setPosicionActual(coordenadas);
                        if (mapaInstancia.current) {
                            mapaInstancia.current.getView().setCenter(fromLonLat(coordenadas));
                        }
                    });
                }
            }
        }
    }, [isTracking]);

    const handleStartSectorTracking = useCallback((sector) => {
        if (!socketService.socket || !isSocketConnected) {
            alert('Socket no conectado. Por favor, activa el GPS primero.');
            return;
        }

        const confirmStart = window.confirm(
            `¿Deseas iniciar el tracking del sector "${sector.name}"?\n\n` +
            `El sistema te guiará por ${sector.coordinates.length} puntos de la ruta.\n` +
            `Podrás hacer clic en cada punto para marcarlos como completados.`
        );

        if (confirmStart) {
            console.log('🎯 Iniciando tracking de sector:', sector._id);

            if (sectorProgressLayerRef.current) {
              
                if (sectorProgressLayerRef.current.linesLayer) {
                    mapaInstancia.current.removeLayer(sectorProgressLayerRef.current.linesLayer);
                }
                if (sectorProgressLayerRef.current.pointsLayer) {
                    mapaInstancia.current.removeLayer(sectorProgressLayerRef.current.pointsLayer);
                }
                sectorProgressLayerRef.current = null;
            }

            socketService.socket.emit('sector:start-tracking', { sectorId: sector._id });

            if (!isTracking) {
                handleToggleTracking();
            }

            if (sector.coordinates && sector.coordinates.length > 0) {
                const coords = fromLonLat(sector.coordinates[0]);
                mapaInstancia.current.getView().setCenter(coords);
                mapaInstancia.current.getView().setZoom(18);
            }

            setSectorSeleccionado(null);
        }
    }, [isSocketConnected, isTracking, handleToggleTracking]);

    const handleStopSectorTracking = useCallback(() => {
        if (!socketService.socket) return;

        const confirmStop = window.confirm('¿Deseas detener el tracking del sector?');

        if (confirmStop) {
            console.log('🛑 Deteniendo tracking de sector');
            socketService.socket.emit('sector:stop-tracking');
            setActiveSectorTracking(null);

            if (sectorProgressLayerRef.current) {
                if (sectorProgressLayerRef.current.linesLayer) {
                    mapaInstancia.current.removeLayer(sectorProgressLayerRef.current.linesLayer);
                }
                if (sectorProgressLayerRef.current.pointsLayer) {
                    mapaInstancia.current.removeLayer(sectorProgressLayerRef.current.pointsLayer);
                }
                sectorProgressLayerRef.current = null;
            }
        }
    }, []);

    const handleCancelCompletion = useCallback(() => {
        setShowCompletionDialog(false);
    }, []);

    const centrarEnUsuario = useCallback((userId) => {
        const location = userLocations[userId];
        if (location && mapaInstancia.current) {
            const coords = fromLonLat([location.longitude, location.latitude]);
            mapaInstancia.current.getView().setCenter(coords);
            mapaInstancia.current.getView().setZoom(17);
        }
    }, [userLocations]);

    const fetchSectors = useCallback(async () => {
    setCargando(true);
    
    try {
        const hoy = new Date();
        const tresDiasDespues = new Date();
        tresDiasDespues.setDate(hoy.getDate() + 3);
        const formatoFecha = (fecha) => fecha.toISOString().split("T")[0];

        console.log('📍 Rol del usuario:', currentUserRole);

        // 1️⃣ Obtener sectores del backend
        const response = await apiService.get(
            `/api/sectores/sectores?fechaInicio=${formatoFecha(hoy)}&fechaFin=${formatoFecha(tresDiasDespues)}`
        );

        console.log(`📦 Sectores recibidos del backend: ${response.data?.length || 0}`);

        let sectoresFinales = response.data || [];

        // 2️⃣ FILTRO: Si es supervisor, obtener su brigada y filtrar
        if (isSupervisor) {
            try {
                console.log('🔍 Obteniendo información del supervisor...');
                const userResponse = await apiService.get('/api/auth/me');
                
                console.log('👤 Respuesta de /api/auth/me:', userResponse.data);

                // Verificar estructura de respuesta
                if (!userResponse.data) {
                    throw new Error('Respuesta vacía del servidor');
                }

                const userData = userResponse.data;
                const brigada = userData.brigada || userData.brigadeId;

                if (!brigada || !brigada._id) {
                    console.log('⚠️ Supervisor sin brigada asignada');
                    alert('No tienes una brigada asignada. Contacta al administrador para que te asigne una brigada.');
                    setSectors([]);
                    return;
                }

                const brigadaId = brigada._id;
                const brigadaNombre = brigada.name || brigada.nombre || 'Sin nombre';
                
                console.log('🔒 Brigada del supervisor:', {
                    id: brigadaId,
                    nombre: brigadaNombre
                });

                // Filtrar sectores por brigada del supervisor
                sectoresFinales = response.data.filter(sector => {
                    // Manejar diferentes estructuras de assignedBrigade
                    let sectorBrigadeId = null;
                    
                    if (sector.assignedBrigade) {
                        if (typeof sector.assignedBrigade === 'object') {
                            sectorBrigadeId = sector.assignedBrigade._id || sector.assignedBrigade.id;
                        } else {
                            sectorBrigadeId = sector.assignedBrigade;
                        }
                    }

                    const match = sectorBrigadeId?.toString() === brigadaId.toString();
                    
                    if (match) {
                        console.log(`✅ Sector "${sector.name}" incluido (brigada: ${brigadaNombre})`);
                    } else {
                        console.log(`🚫 Sector "${sector.name}" filtrado (brigada diferente)`);
                    }
                    
                    return match;
                });

                console.log(`✅ Sectores filtrados para brigada "${brigadaNombre}": ${sectoresFinales.length}`);

                if (sectoresFinales.length === 0) {
                    console.log('⚠️ No hay sectores asignados a tu brigada en estas fechas');
                }

            } catch (userError) {
                console.error('❌ Error al obtener información del usuario:', userError);
                console.error('Detalles del error:', userError.response?.data || userError.message);
                
                if (userError.response?.status === 401) {
                    alert('Tu sesión ha expirado. Por favor, inicia sesión nuevamente.');
                    // Opcional: redirigir al login
                    // window.location.href = '/login';
                } else {
                    alert('Error al verificar tu brigada. Por favor, recarga la página o contacta al administrador.');
                }
                
                setSectors([]);
                return;
            }

        } else if (isAdmin) {
            // 3️⃣ ADMIN: Ver todos los sectores
            console.log('👨‍💼 Administrador: Mostrando todos los sectores');
            sectoresFinales = response.data || [];
            console.log(`📊 Total sectores para admin: ${sectoresFinales.length}`);

        } else {
            // 4️⃣ USUARIO REGULAR: Ver sectores disponibles
            console.log('👤 Usuario regular: Mostrando sectores disponibles');
            sectoresFinales = response.data || [];
            console.log(`📊 Total sectores para usuario: ${sectoresFinales.length}`);
        }

        // 5️⃣ Ordenar y establecer sectores
        if (!Array.isArray(sectoresFinales)) {
            console.error('❌ sectoresFinales no es un array:', sectoresFinales);
            sectoresFinales = [];
        }

        const sortedSectors = sectoresFinales.sort((a, b) => {
            if (!a.name || !b.name) return 0;
            return a.name.localeCompare(b.name);
        });

        setSectors(sortedSectors);
        console.log(`📊 Total sectores mostrados en el mapa: ${sortedSectors.length}`);

    } catch (error) {
        console.error("❌ Error al obtener los sectores:", error);
        console.error('Detalles del error:', error.response?.data || error.message);
        
        // Manejo específico de errores
        if (error.response?.status === 403) {
            alert('No tienes permisos para ver sectores. Contacta al administrador.');
        } else if (error.response?.status === 401) {
            alert('Tu sesión ha expirado. Por favor, inicia sesión nuevamente.');
            // Opcional: redirigir al login
            // window.location.href = '/login';
        } else if (error.response?.status === 500) {
            alert('Error del servidor. Por favor, intenta de nuevo en unos momentos.');
        } else if (!error.response) {
            alert('Error de conexión. Verifica tu conexión a internet.');
        } else {
            alert('Error al cargar los sectores. Por favor, recarga la página.');
        }
        
        setSectors([]);
        
    } finally {
        setCargando(false);
    }
}, [currentUserRole, isSupervisor, isAdmin]);



    
    const handleCompleteSector = useCallback(async () => {
        if (!completedSectorData) return;

        try {
            setCargando(true);

            const response = await apiService.put(
                `/api/sectores/${completedSectorData.sectorId}/complete`,
                {
                    duration: completedSectorData.duration,
                    comment: 'Sector completado mediante tracking GPS con verificación de puntos'
                }
            );

            if (response.data.success) {
                console.log('✅ Sector completado exitosamente');

                if (socketService.socket) {
                    socketService.socket.emit('sector:stop-tracking');
                }

                setActiveSectorTracking(null);
                setShowCompletionDialog(false);
                setCompletedSectorData(null);

                if (sectorProgressLayerRef.current) {
                    if (sectorProgressLayerRef.current.linesLayer) {
                        mapaInstancia.current.removeLayer(sectorProgressLayerRef.current.linesLayer);
                    }
                    if (sectorProgressLayerRef.current.pointsLayer) {
                        mapaInstancia.current.removeLayer(sectorProgressLayerRef.current.pointsLayer);
                    }
                    sectorProgressLayerRef.current = null;
                }

                setTimeout(() => {
                    fetchSectors();
                }, 500);

                alert(`¡Sector "${completedSectorData.sectorName}" completado exitosamente!`);
            }

        } catch (error) {
            console.error('Error completando sector:', error);
            alert('Error al completar el sector. Por favor, intenta de nuevo.');
        } finally {
            setCargando(false);
        }
    }, [completedSectorData, fetchSectors]);

    const findSectorByCoordinates = useCallback((lonLat) => {
        return sectors.find((s) => {
            return s.coordinates?.some((coord) => {
                const distance = getDistance(coord, lonLat)
                return distance < 50
            })
        })
    }, [sectors])

    const limpiarMarcadoresTemporales = useCallback(() => {
        if (marcadoresLayer) marcadoresLayer.getSource().clear()
        if (lineaLayer) lineaLayer.getSource().clear()
        setCoordenadasTemporal([])
    }, [marcadoresLayer, lineaLayer])

    // ⚡ MODIFICADO: agregarMarcadorTemporal con color según modo
    const agregarMarcadorTemporal = useCallback((coordenadasLonLat) => {
        if (!marcadoresLayer) return
        const punto = new Feature({
            geometry: new Point(fromLonLat(coordenadasLonLat)),
        })
        
        // ⚡ Usar color diferente si está editando
        const markerColor = editingSector ? "#e67e22" : COLORS.newMarker
        
        punto.setStyle(
            new Style({
                image: new CircleStyle({
                    radius: 8,
                    fill: new Fill({ color: markerColor }),
                    stroke: new Stroke({ color: COLORS.white, width: 3 }),
                }),
            })
        )
        marcadoresLayer.getSource().addFeature(punto)
    }, [marcadoresLayer, editingSector])

    // ⚡ MODIFICADO: actualizarLineaTemporal con color según modo
    const actualizarLineaTemporal = useCallback((coordenadas) => {
        if (!lineaLayer || coordenadas.length < 2) return
        lineaLayer.getSource().clear()
        
        const lineaGeom = new LineString(coordenadas.map((coord) => fromLonLat(coord)))
        const lineaFeature = new Feature({ geometry: lineaGeom })
        
        // ⚡ Usar color diferente si está editando
        const lineColor = editingSector ? "#e67e22" : COLORS.newMarker
        
        lineaFeature.setStyle(
            new Style({
                stroke: new Stroke({
                    color: lineColor,
                    width: 3,
                    lineDash: [10, 5],
                }),
            })
        )
        lineaLayer.getSource().addFeature(lineaFeature)
    }, [lineaLayer, editingSector])

    const deshacerUltimoPunto = useCallback(() => {
        if (coordenadasTemporal.length === 0) return
        const nuevasCoordenadas = coordenadasTemporal.slice(0, -1)
        setCoordenadasTemporal(nuevasCoordenadas)
        if (marcadoresLayer) {
            marcadoresLayer.getSource().clear()
            nuevasCoordenadas.forEach(coord => {
                const punto = new Feature({
                    geometry: new Point(fromLonLat(coord)),
                })
                const markerColor = editingSector ? "#e67e22" : COLORS.newMarker
                punto.setStyle(
                    new Style({
                        image: new CircleStyle({
                            radius: 8,
                            fill: new Fill({ color: markerColor }),
                            stroke: new Stroke({ color: COLORS.white, width: 3 }),
                        }),
                    })
                )
                marcadoresLayer.getSource().addFeature(punto)
            })
        }
        if (lineaLayer) {
            lineaLayer.getSource().clear()
            if (nuevasCoordenadas.length >= 2) {
                const lineColor = editingSector ? "#e67e22" : COLORS.newMarker
                const lineaGeom = new LineString(nuevasCoordenadas.map((coord) => fromLonLat(coord)))
                const lineaFeature = new Feature({ geometry: lineaGeom })
                lineaFeature.setStyle(
                    new Style({
                        stroke: new Stroke({
                            color: lineColor,
                            width: 3,
                            lineDash: [10, 5],
                        }),
                    })
                )
                lineaLayer.getSource().addFeature(lineaFeature)
            }
        }
        // Actualizar coordenadas si está editando
        if (editingSector) {
            setNewSectorCoordinates(nuevasCoordenadas)
        }
    }, [coordenadasTemporal, marcadoresLayer, lineaLayer, editingSector])

    // ⚡ MODIFICADO: completarDibujo
    const completarDibujo = useCallback(() => {
        if (coordenadasTemporal.length < 1) {
            alert("Debes agregar al menos un punto")
            return
        }
        setNewSectorCoordinates(coordenadasTemporal)
        setShowSectorForm(true)
        setModoEdicion(false)
        document.body.style.cursor = "default"
    }, [coordenadasTemporal])

    const mostrarInfoSector = useCallback((sector, coordenadas) => {
        if (!infoOverlay) return
        const overlayElement = infoOverlay.getElement()
        if (!overlayElement) return
        const statusColor = getStatusColor(sector?.status)
        overlayElement.innerHTML = `
            <div class="popup-container">
                <div class="popup-header" style="background-color: ${statusColor};">
                    <h3>${sector.name}</h3>
                </div>
                <div class="popup-body">
                    <p><strong>Estado:</strong> ${sector.status}</p>
                    ${sector.assignmentAccepted ? `<p><strong>Aceptada:</strong> Sí</p>` : ""}
                    <button class="popup-button">Ver detalles</button>
                </div>
            </div>
        `
        const button = overlayElement.querySelector(".popup-button")
        if (button) {
            button.onclick = () => setSectorSeleccionado(sector)
        }
        infoOverlay.setPosition(fromLonLat(coordenadas))
    }, [infoOverlay])

    const handleMouseMove = useCallback((event) => {
        if (modoEdicion) {
            document.body.style.cursor = "crosshair"
            return
        }
        const map = mapaInstancia.current
        if (!map) return
        const feature = map.forEachFeatureAtPixel(event.pixel, (feature) => feature)
        if (feature) {
            if (feature.get('isClickable') && activeSectorTracking) {
                document.body.style.cursor = "pointer"
                return
            }

            const featureCoordinates = feature.getGeometry().getCoordinates()
            const featureLonLat = toLonLat(featureCoordinates)
            const sector = findSectorByCoordinates(featureLonLat)
            if (sector) {
                document.body.style.cursor = "pointer"
                mostrarInfoSector(sector, featureLonLat)
            } else {
                document.body.style.cursor = "default"
                if (infoOverlay) infoOverlay.setPosition(undefined)
            }
        } else {
            document.body.style.cursor = "default"
            if (infoOverlay) infoOverlay.setPosition(undefined)
        }
    }, [modoEdicion, findSectorByCoordinates, mostrarInfoSector, infoOverlay, activeSectorTracking])

    // ⚡ NUEVA FUNCIÓN: Eliminar punto específico
    const handleDeletePoint = useCallback((coordinateIndex) => {
        if (coordenadasTemporal.length <= 1) {
            alert('Debe haber al menos 1 punto en el sector')
            return
        }

        const confirmDelete = window.confirm(`¿Deseas eliminar el punto ${coordinateIndex + 1}?`)
        if (!confirmDelete) return

        const nuevasCoordenadas = coordenadasTemporal.filter((_, index) => index !== coordinateIndex)
        setCoordenadasTemporal(nuevasCoordenadas)

        // Redibujar marcadores
        if (marcadoresLayer) {
            marcadoresLayer.getSource().clear()
            nuevasCoordenadas.forEach(coord => {
                const punto = new Feature({
                    geometry: new Point(fromLonLat(coord)),
                })
                const markerColor = editingSector ? "#e67e22" : COLORS.newMarker
                punto.setStyle(
                    new Style({
                        image: new CircleStyle({
                            radius: 8,
                            fill: new Fill({ color: markerColor }),
                            stroke: new Stroke({ color: COLORS.white, width: 3 }),
                        }),
                    })
                )
                marcadoresLayer.getSource().addFeature(punto)
            })
        }

        // Redibujar línea
        if (lineaLayer) {
            lineaLayer.getSource().clear()
            if (nuevasCoordenadas.length >= 2) {
                const lineColor = editingSector ? "#e67e22" : COLORS.newMarker
                const lineaGeom = new LineString(nuevasCoordenadas.map((coord) => fromLonLat(coord)))
                const lineaFeature = new Feature({ geometry: lineaGeom })
                lineaFeature.setStyle(
                    new Style({
                        stroke: new Stroke({
                            color: lineColor,
                            width: 3,
                            lineDash: [10, 5],
                        }),
                    })
                )
                lineaLayer.getSource().addFeature(lineaFeature)
            }
        }

        // Actualizar las coordenadas del sector en edición
        if (editingSector) {
            setNewSectorCoordinates(nuevasCoordenadas)
        }
    }, [coordenadasTemporal, marcadoresLayer, lineaLayer, editingSector])

    // ⚡ MODIFICADO: handleMapClick con soporte para clic derecho
    const handleMapClick = useCallback((event) => {
        if (isSupervisor) {
            const feature = mapaInstancia.current.forEachFeatureAtPixel(event.pixel, (feature) => feature)
            if (feature) {
                const featureCoordinates = feature.getGeometry().getCoordinates()
                const featureLonLat = toLonLat(featureCoordinates)
                const sector = findSectorByCoordinates(featureLonLat)
                if (sector) {
                    setSectorSeleccionado(sector)
                    mostrarInfoSector(sector, featureLonLat)
                }
            }
            return;
        }

        if (modoEdicion) {
            // ⚡ DETECTAR CLIC DERECHO PARA ELIMINAR PUNTO
            if (event.originalEvent.button === 2) { // Botón derecho
                event.preventDefault()
                
                // Verificar si se hizo clic sobre un marcador existente
                const feature = mapaInstancia.current.forEachFeatureAtPixel(event.pixel, (feature) => feature)
                if (feature && marcadoresLayer.getSource().hasFeature(feature)) {
                    // Encontrar el índice del punto clickeado
                    const clickedCoords = toLonLat(feature.getGeometry().getCoordinates())
                    const index = coordenadasTemporal.findIndex(coord => {
                        const distance = getDistance(coord, clickedCoords)
                        return distance < 10 // 10 metros de tolerancia
                    })
                    
                    if (index !== -1) {
                        handleDeletePoint(index)
                    }
                }
                return
            }

            // ⚡ CLIC IZQUIERDO: Agregar nuevo punto
            const coordenadas = mapaInstancia.current.getCoordinateFromPixel(event.pixel)
            const coordenadasLonLat = toLonLat(coordenadas)
            const nuevasCoordenadas = [...coordenadasTemporal, coordenadasLonLat]
            setCoordenadasTemporal(nuevasCoordenadas)
            agregarMarcadorTemporal(coordenadasLonLat)
            actualizarLineaTemporal(nuevasCoordenadas)
            
            // Actualizar coordenadas si está editando
            if (editingSector) {
                setNewSectorCoordinates(nuevasCoordenadas)
            }
        } else {
            const feature = mapaInstancia.current.forEachFeatureAtPixel(event.pixel, (feature) => feature)
            if (feature) {
                const featureCoordinates = feature.getGeometry().getCoordinates()
                const featureLonLat = toLonLat(featureCoordinates)
                const sector = findSectorByCoordinates(featureLonLat)
                if (sector) {
                    setSectorSeleccionado(sector)
                    mostrarInfoSector(sector, featureLonLat)
                }
            }
        }
    }, [modoEdicion, coordenadasTemporal, findSectorByCoordinates, mostrarInfoSector, agregarMarcadorTemporal, actualizarLineaTemporal, isSupervisor, editingSector, marcadoresLayer, handleDeletePoint])

    const createCustomMarker = useCallback((coordinates) => {
        if (!mapaInstancia.current) return
        if (customMarker && customMarker.getLayer()) {
            mapaInstancia.current.removeLayer(customMarker.getLayer())
        }
        const point = new Feature({
            geometry: new Point(fromLonLat(coordinates)),
        })
        point.setStyle(
            new Style({
                image: new CircleStyle({
                    radius: 8,
                    fill: new Fill({ color: COLORS.customMarker }),
                    stroke: new Stroke({ color: COLORS.white, width: 2 }),
                }),
            })
        )
        const markerLayer = new VectorLayer({
            source: new VectorSource({ features: [point] }),
            zIndex: 1000,
        })
        mapaInstancia.current.addLayer(markerLayer)
        setCustomMarker({ feature: point, getLayer: () => markerLayer })
    }, [customMarker])

    const handleAddMarker = useCallback(() => {
        if (customMarker && customMarker.getLayer()) {
            mapaInstancia.current.removeLayer(customMarker.getLayer())
            setCustomMarker(null)
            setShowSectorForm(false)
        }
        document.body.style.cursor = "crosshair"
        if (clickListenerRef.current) {
            mapaInstancia.current.un("click", clickListenerRef.current)
        }
        const clickListener = (e) => {
            const coords = mapaInstancia.current.getCoordinateFromPixel(e.pixel)
            const lonLat = toLonLat(coords)
            createCustomMarker(lonLat)
            setNewSectorCoordinates([lonLat])
            setShowSectorForm(true)
            document.body.style.cursor = "default"
            mapaInstancia.current.un("click", clickListener)
            clickListenerRef.current = null
        }
        clickListenerRef.current = clickListener
        mapaInstancia.current.once("click", clickListener)
    }, [customMarker, createCustomMarker])

    const crearActualizarMarcadorUsuario = useCallback((coordenadas) => {
        if (!mapaInstancia.current) return;

        console.log('🔵 Creando/Actualizando marcador usuario en:', coordenadas);

        if (userLocationMarker && userLocationMarker.getLayer()) {
            mapaInstancia.current.removeLayer(userLocationMarker.getLayer());
        }

        const punto = new Feature({
            geometry: new Point(fromLonLat(coordenadas)),
        });

        punto.setStyle(
            new Style({
                image: new CircleStyle({
                    radius: 10,
                    fill: new Fill({ color: COLORS.userLocation }),
                    stroke: new Stroke({ color: "white", width: 3 }),
                }),
                text: new Text({
                    text: 'YO',
                    fill: new Fill({ color: COLORS.white }),
                    font: 'bold 10px Arial',
                    offsetY: 0,
                })
            })
        );

        const capaUsuario = new VectorLayer({
            source: new VectorSource({ features: [punto] }),
            zIndex: 999,
        });

        mapaInstancia.current.addLayer(capaUsuario);
        setUserLocationMarker({ feature: punto, getLayer: () => capaUsuario });

        const markerElement = document.createElement("div");
        markerElement.className = "user-location-pulse";
        const pulseOverlay = new Overlay({
            element: markerElement,
            positioning: "center-center",
            offset: [0, 0],
            position: fromLonLat(coordenadas),
        });
        mapaInstancia.current.addOverlay(pulseOverlay);

    }, [userLocationMarker]);

    const obtenerUbicacion = useCallback(() => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const { latitude, longitude } = position.coords
                    const coordenadas = [longitude, latitude]
                    setPosicionActual(coordenadas)
                    if (mapaInstancia.current) {
                        mapaInstancia.current.getView().setCenter(fromLonLat(coordenadas))
                        crearActualizarMarcadorUsuario(coordenadas)
                    }
                },
                (error) => console.error("Error al obtener la ubicación:", error),
                { enableHighAccuracy: true, maximumAge: 10000, timeout: 5000 }
            )
        }
    }, [crearActualizarMarcadorUsuario])

    const dibujarSectoresEnMapa = useCallback(() => {
        if (!mapaInstancia.current) return
        if (ultimaCapaRef.current) {
            mapaInstancia.current.removeLayer(ultimaCapaRef.current)
        }
        const features = sectors
            .filter((sector) => sector.coordinates && sector.coordinates.length > 0)
            .map((sector) => {
                const coordenadas = sector.coordinates.map((coord) => fromLonLat([coord[0], coord[1]]))
                if (coordenadas.length > 1) {
                    const polilinea = new Feature({
                        geometry: new LineString(coordenadas),
                        sector: sector,
                    })
                    const strokeColor = getStatusColor(sector.status)
                    polilinea.setStyle(
                        new Style({
                            stroke: new Stroke({
                                color: strokeColor,
                                width: 3,
                                lineDash: sector.status === "Proceso" ? [5, 5] : undefined,
                            }),
                        })
                    )
                    return polilinea
                } else {
                    const marcador = new Feature({
                        geometry: new Point(coordenadas[0]),
                        sector: sector,
                    })
                    const marcadorColor = getStatusColor(sector.status)
                    marcador.setStyle(
                        new Style({
                            image: new CircleStyle({
                                radius: 8,
                                fill: new Fill({ color: marcadorColor }),
                                stroke: new Stroke({ color: COLORS.white, width: 2 }),
                            }),
                        })
                    )
                    return marcador
                }
            })
        const nuevaCapa = new VectorLayer({
            source: new VectorSource({ features: features }),
        })
        mapaInstancia.current.addLayer(nuevaCapa)
        ultimaCapaRef.current = nuevaCapa
    }, [sectors])

    const handleBusqueda = useCallback((e) => {
        const valor = e.target.value
        setBusqueda(valor)
        if (valor.length > 0) {
            const coincidencias = sectors.filter((sector) =>
                sector.name.toLowerCase().includes(valor.toLowerCase())
            )
            setSugerencias(coincidencias)
        } else {
            setSugerencias([])
        }
    }, [sectors])

    const seleccionarSector = useCallback((sector) => {
        setBusqueda(sector.name)
        setSugerencias([])
        setSectorSeleccionado(sector)
        if (sector.coordinates && sector.coordinates.length && mapaInstancia.current) {
            const coordenadas = fromLonLat(sector.coordinates[0])
            mapaInstancia.current.getView().setCenter(coordenadas)
            mapaInstancia.current.getView().setZoom(17)
        }
    }, [])

    const centrarMapa = useCallback(() => {
        if (posicionActual && mapaInstancia.current) {
            mapaInstancia.current.getView().setCenter(fromLonLat(posicionActual))
            mapaInstancia.current.getView().setZoom(16)
        } else {
            obtenerUbicacion()
        }
    }, [posicionActual, obtenerUbicacion])

    const resetearVista = useCallback(() => {
        if (!mapaInstancia.current) return
        mapaInstancia.current.getView().setCenter(fromLonLat([LONGITUD_CENTRO, LATITUD_CENTRO]))
        mapaInstancia.current.getView().setZoom(ZOOM)
        setSectorSeleccionado(null)
        setBusqueda("")
        setSugerencias([])
        if (infoOverlay) infoOverlay.setPosition(undefined)
    }, [infoOverlay])

    // ⚡ MODIFICADO: handleCancelSectorForm
    const handleCancelSectorForm = useCallback(() => {
        setShowSectorForm(false)
        setNewSectorCoordinates(null)
        setEditingSector(null)
        setModoEdicion(false) // ⚡ AGREGAR ESTA LÍNEA
        if (customMarker && customMarker.getLayer()) {
            mapaInstancia.current.removeLayer(customMarker.getLayer())
        }
        setCustomMarker(null)
        limpiarMarcadoresTemporales()
        document.body.style.cursor = "default"
    }, [customMarker, limpiarMarcadoresTemporales])


    const handleCreateSectorFromMarker = useCallback(async (sectorData) => {
        try {
            setCargando(true)
            const datosCompletos = {
                ...sectorData,
                coordinates: newSectorCoordinates,
            }
            await apiService.post("/api/sectores", datosCompletos)
            await fetchSectors()
            if (customMarker && customMarker.getLayer()) {
                mapaInstancia.current.removeLayer(customMarker.getLayer())
            }
            setCustomMarker(null)
            setShowSectorForm(false)
            setNewSectorCoordinates(null)
            limpiarMarcadoresTemporales()
        } catch (error) {
            console.error("Error al guardar el sector:", error)
            alert("Error al guardar el sector. Por favor, intenta nuevamente.")
        } finally {
            setCargando(false)
        }
    }, [newSectorCoordinates, customMarker, fetchSectors, limpiarMarcadoresTemporales])

    const handleUpdateSector = useCallback(async (sectorData) => {
        try {
            setCargando(true)
            const datosCompletos = {
                ...sectorData,
                coordinates: newSectorCoordinates || editingSector.coordinates,
            }
            await apiService.put(`/api/sectores/${editingSector._id}`, datosCompletos)
            await fetchSectors()
            if (customMarker && customMarker.getLayer()) {
                mapaInstancia.current.removeLayer(customMarker.getLayer())
            }
            setCustomMarker(null)
            setShowSectorForm(false)
            setNewSectorCoordinates(null)
            setEditingSector(null)
            setSectorSeleccionado(null)
            limpiarMarcadoresTemporales()
            setModoEdicion(false) // ⚡ AGREGAR
        } catch (error) {
            console.error("Error al actualizar el sector:", error)
            alert("Error al actualizar el sector. Por favor, intenta nuevamente.")
        } finally {
            setCargando(false)
        }
    }, [newSectorCoordinates, editingSector, customMarker, fetchSectors, limpiarMarcadoresTemporales])

    // ⚡ MODIFICADO: handleEditSector con modo edición interactivo
    const handleEditSector = useCallback((sector) => {
        setEditingSector(sector)
        setNewSectorCoordinates(sector.coordinates)
        setSectorSeleccionado(null)
        
        if (infoOverlay) {
            infoOverlay.setPosition(undefined)
        }

        // ⚡ NUEVO: Cargar coordenadas existentes en el array temporal
        setCoordenadasTemporal([...sector.coordinates])

        // ⚡ NUEVO: Habilitar modo edición para permitir modificar puntos
        setModoEdicion(true)

        // Limpiar marcadores anteriores
        if (marcadoresLayer) {
            marcadoresLayer.getSource().clear()
        }
        if (lineaLayer) {
            lineaLayer.getSource().clear()
        }

        // Dibujar puntos existentes como marcadores editables
        if (sector.coordinates && sector.coordinates.length > 0) {
            sector.coordinates.forEach(coord => {
                const punto = new Feature({
                    geometry: new Point(fromLonLat(coord)),
                })
                punto.setStyle(
                    new Style({
                        image: new CircleStyle({
                            radius: 8,
                            fill: new Fill({ color: "#e67e22" }),
                            stroke: new Stroke({ color: COLORS.white, width: 3 }),
                        }),
                    })
                )
                if (marcadoresLayer) {
                    marcadoresLayer.getSource().addFeature(punto)
                }
            })

            // Dibujar línea conectando los puntos
            if (sector.coordinates.length > 1 && lineaLayer) {
                const lineaGeom = new LineString(sector.coordinates.map((coord) => fromLonLat(coord)))
                const lineaFeature = new Feature({ geometry: lineaGeom })
                lineaFeature.setStyle(
                    new Style({
                        stroke: new Stroke({
                            color: "#e67e22",
                            width: 3,
                            lineDash: [10, 5],
                        }),
                    })
                )
                lineaLayer.getSource().addFeature(lineaFeature)
            }

            // Centrar el mapa en el primer punto
            mapaInstancia.current.getView().setCenter(fromLonLat(sector.coordinates[0]))
            mapaInstancia.current.getView().setZoom(17)
        }

        //setShowSectorForm(true)
    }, [infoOverlay, marcadoresLayer, lineaLayer])

    const handleDeleteSector = useCallback(async (sector) => {
        const confirmDelete = window.confirm(
            `¿Estás seguro de que deseas eliminar el sector "${sector.name}"? Esta acción no se puede deshacer.`
        )
        if (!confirmDelete) return
        try {
            setCargando(true)
            await apiService.delete(`/api/sectores/${sector._id}`)
            await fetchSectors()
            setSectorSeleccionado(null)
            if (infoOverlay) {
                infoOverlay.setPosition(undefined)
            }
        } catch (error) {
            console.error("Error al eliminar el sector:", error)
            alert("Error al eliminar el sector. Por favor, intenta nuevamente.")
        } finally {
            setCargando(false)
        }
    }, [fetchSectors, infoOverlay])

    // ============================================
    // USE EFFECTS - ORDEN CORRECTO
    // ============================================

    // 1️⃣ PRIMERO: Inicializar Socket.IO
    useEffect(() => {
        console.log('🔌 Inicializando Socket.IO...');
        const socket = socketService.connect();

        if (socket) {
            setIsSocketConnected(true);

            const handleLocationUpdate = (data) => {
                console.log('📍 Ubicación actualizada:', data);
                setUserLocations(prev => ({
                    ...prev,
                    [data.userId]: {
                        userId: data.userId,
                        username: data.username,
                        role: data.role,
                        latitude: data.latitude,
                        longitude: data.longitude,
                        accuracy: data.accuracy,
                        speed: data.speed,
                        heading: data.heading,
                        timestamp: data.timestamp
                    }
                }));
            };

            const handleUsersOnline = (data) => {
                console.log('👥 Usuarios online:', data);
                setOnlineUsers(data.users || []);
            };

            socketService.onLocationUpdate(handleLocationUpdate);
            socketService.onUsersOnline(handleUsersOnline);

            socketService.getAllLocations((locations) => {
                console.log('📍 Ubicaciones iniciales:', locations);
                const locationsMap = {};
                locations.forEach(loc => {
                    locationsMap[loc.userId] = loc;
                });
                setUserLocations(locationsMap);
            });

            return () => {
                socketService.offLocationUpdate(handleLocationUpdate);
                socketService.offUsersOnline(handleUsersOnline);
            };
        } else {
            console.log('❌ No se pudo conectar el socket');
        }
    }, []);

    // 2️⃣ Inicializar mapa
    useEffect(() => {
        if (!mapaRef.current) return;

        mapaInstancia.current = new Map({
            target: mapaRef.current,
            layers: [new TileLayer({ source: new OSM() })],
            view: new View({
                center: fromLonLat([LONGITUD_CENTRO, LATITUD_CENTRO]),
                zoom: ZOOM,
            }),
        })

        const markerSource = new VectorSource()
        const markerLayerInstance = new VectorLayer({
            source: markerSource,
            zIndex: 100,
        })
        mapaInstancia.current.addLayer(markerLayerInstance)
        setMarcadoresLayer(markerLayerInstance)

        const lineSource = new VectorSource()
        const lineLayerInstance = new VectorLayer({
            source: lineSource,
            zIndex: 99,
            style: new Style({
                stroke: new Stroke({
                    color: COLORS.newMarker,
                    width: 2,
                    lineDash: [5, 5],
                }),
            }),
        })
        mapaInstancia.current.addLayer(lineLayerInstance)
        setLineaLayer(lineLayerInstance)

        fetchSectors()
        obtenerUbicacion()

        return () => {
            if (mapaInstancia.current) {
                mapaInstancia.current.removeLayer(markerLayerInstance)
                mapaInstancia.current.removeLayer(lineLayerInstance)
                mapaInstancia.current.setTarget(null)
            }
        }
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    // 3️⃣ Overlay de info
    useEffect(() => {
        if (!mapaInstancia.current) return
        const overlayDiv = document.createElement("div")
        overlayDiv.className = "sector-info-overlay"
        const overlay = new Overlay({
            element: overlayDiv,
            positioning: "bottom-center",
            offset: [0, -10],
            stopEvent: true,
        })
        mapaInstancia.current.addOverlay(overlay)
        setInfoOverlay(overlay)
        return () => {
            if (mapaInstancia.current && overlay) {
                mapaInstancia.current.removeOverlay(overlay)
            }
        }
    }, [])

    // 4️⃣ Dibujar sectores
    useEffect(() => {
        if (sectors.length > 0) {
            dibujarSectoresEnMapa()
        }
    }, [sectors, dibujarSectoresEnMapa])

    // 5️⃣ Event listeners del mapa
    useEffect(() => {
        if (!mapaInstancia.current) return
        mapaInstancia.current.on("click", handleMapClick)
        mapaInstancia.current.on("pointermove", handleMouseMove)
        return () => {
            if (mapaInstancia.current) {
                mapaInstancia.current.un("click", handleMapClick)
                mapaInstancia.current.un("pointermove", handleMouseMove)
            }
        }
    }, [handleMapClick, handleMouseMove])

    // 6️⃣ Click fuera del search
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (searchRef.current && !searchRef.current.contains(event.target)) {
                setSugerencias([])
            }
        }
        document.addEventListener("mousedown", handleClickOutside)
        return () => {
            document.removeEventListener("mousedown", handleClickOutside)
        }
    }, [])

    // 7️⃣ Cargar brigades y circunscripciones
    useEffect(() => {
        const fetchBrigadesAndCircunscripciones = async () => {
            try {
                const [brigadesResponse, circResponse] = await Promise.all([
                    apiService.get(`/api/brigadas`),
                    apiService.get(`/api/circunscripciones`),
                ])
                setBrigades(brigadesResponse.data)
                setCircunscripciones(circResponse.data)
            } catch (error) {
                console.error("Error al obtener datos:", error)
            }
        }
        fetchBrigadesAndCircunscripciones()
    }, [])

    // 8️⃣ Dibujar ubicaciones de usuarios
    useEffect(() => {
        if (!mapaInstancia.current || Object.keys(userLocations).length === 0) return;

        if (userLocationsLayerRef.current) {
            mapaInstancia.current.removeLayer(userLocationsLayerRef.current);
        }

        const features = Object.values(userLocations).map((location) => {
            const feature = new Feature({
                geometry: new Point(fromLonLat([location.longitude, location.latitude])),
                userId: location.userId,
                username: location.username,
                role: location.role
            });

            const color = getUserColor(location.userId, userLocations);

            feature.setStyle(
                new Style({
                    image: new CircleStyle({
                        radius: 10,
                        fill: new Fill({ color: color }),
                        stroke: new Stroke({
                            color: COLORS.white,
                            width: 3
                        }),
                    }),
                    text: new Text({
                        text: location.username?.substring(0, 2).toUpperCase() || '??',
                        fill: new Fill({ color: COLORS.white }),
                        font: 'bold 12px Arial',
                        offsetY: 0
                    })
                })
            );

            return feature;
        });

        const userLocationsLayer = new VectorLayer({
            source: new VectorSource({ features }),
            zIndex: 998,
        });

        mapaInstancia.current.addLayer(userLocationsLayer);
        userLocationsLayerRef.current = userLocationsLayer;

    }, [userLocations]);

    // 9️⃣ Actualizar marcador del usuario actual
    useEffect(() => {
        if (!socketService.socket) return;

        const handleLocationUpdate = (data) => {
            console.log('📍 Ubicación recibida:', data);

            setUserLocations(prev => ({
                ...prev,
                [data.userId]: {
                    userId: data.userId,
                    username: data.username,
                    role: data.role,
                    latitude: data.latitude,
                    longitude: data.longitude,
                    accuracy: data.accuracy,
                    speed: data.speed,
                    heading: data.heading,
                    timestamp: data.timestamp
                }
            }));

            const currentUserId = Cookies.get('userId');
            if (data.userId === currentUserId) {
                console.log('🔵 Actualizando marcador del usuario actual');
                const coordenadas = [data.longitude, data.latitude];
                setPosicionActual(coordenadas);
                crearActualizarMarcadorUsuario(coordenadas);

                if (mapaInstancia.current && activeSectorTracking) {
                    mapaInstancia.current.getView().setCenter(fromLonLat(coordenadas));
                }
            }
        };

        socketService.socket.on('location:updated', handleLocationUpdate);

        return () => {
            if (socketService.socket) {
                socketService.socket.off('location:updated', handleLocationUpdate);
            }
        };
    }, [crearActualizarMarcadorUsuario, activeSectorTracking]);

    // 🔟 Escuchar cambios de estado de sectores
    useEffect(() => {
        if (!socketService.socket) return;

        const handleSectorStatusChanged = (data) => {
            console.log('🔄 Estado de sector cambiado:', data);

            setSectors(prevSectors => {
                return prevSectors.map(sector => {
                    if (sector._id === data.sectorId) {
                        return {
                            ...sector,
                            status: data.newStatus,
                            scheduledDate: data.scheduledDate
                        };
                    }
                    return sector;
                });
            });

            if ('Notification' in window && Notification.permission === 'granted') {
                new Notification('✅ Sector Actualizado', {
                    body: data.message,
                    icon: '/logo192.png'
                });
            }
        };

        socketService.socket.on('sector:status-changed', handleSectorStatusChanged);

        return () => {
            if (socketService.socket) {
                socketService.socket.off('sector:status-changed', handleSectorStatusChanged);
            }
        };
    }, []);

    // 1️⃣1️⃣ Eventos de tracking de sector
    useEffect(() => {
        if (!socketService.socket) return;

        const handleSectorTrackingConfirmed = (data) => {
            console.log('✅ ========== TRACKING CONFIRMADO ==========');
            console.log('📊 Data completa recibida:', JSON.stringify(data, null, 2));
            console.log('📊 Puntos completados:', data.completedPoints);
            console.log('📊 Puntos totales:', data.totalPoints);
            console.log('📊 Progreso:', data.progress);
            console.log('📊 Detalles de puntos completados:', data.completedPointsDetails);
            console.log('===========================================');
            
            setActiveSectorTracking({
                ...data,
                startTime: new Date()
            });

            // ⚡ SI HAY PROGRESO EXISTENTE, ESTABLECERLO INMEDIATAMENTE
            if (data.completedPoints > 0) {
                console.log('🔄 Cargando progreso existente...');
                setSectorProgress(prev => ({
                    ...prev,
                    [data.sectorId]: {
                        completedPoints: data.completedPoints,
                        totalPoints: data.totalPoints,
                        progress: data.progress
                    }
                }));
            }

            setTimeout(() => {
                console.log('🎨 Dibujando estado con progreso:', {
                    completedPoints: data.completedPoints || 0,
                    totalPoints: data.totalPoints,
                    progress: data.progress || 0
                });
                
                drawSectorProgress({
                    completedPoints: data.completedPoints || 0,
                    totalPoints: data.totalPoints,
                    progress: data.progress || 0
                });
            }, 100);
        };

        const handleSectorProgress = (data) => {
            console.log('📍 Progreso del sector recibido via socket:', data);

            setSectorProgress(prev => {
                const updated = {
                    ...prev,
                    [data.sectorId]: {
                        completedPoints: data.completedPoints,
                        totalPoints: data.totalPoints,
                        progress: data.progress
                    }
                };
                console.log('📊 Estado de progreso actualizado via socket:', updated);
                return updated;
            });

            setTimeout(() => {
                console.log('🎨 Redibujando desde socket event...');
                drawSectorProgress({
                    completedPoints: data.completedPoints,
                    totalPoints: data.totalPoints,
                    progress: data.progress
                });
            }, 50);
        };

        const handleSectorCompleted = (data) => {
            console.log('🎉 Sector completado:', data);
            setCompletedSectorData(data);
            setShowCompletionDialog(true);
        };

        const handleSectorTrackingStarted = (data) => {
            console.log('📍 Usuario inició tracking:', data);

            // ⚡ ACTUALIZAR PROGRESO SI VIENE EN EL EVENTO
            if (data.completedPoints > 0) {
                console.log('🔄 Actualizando con progreso del evento tracking-started');
                setSectorProgress(prev => ({
                    ...prev,
                    [data.sectorId]: {
                        completedPoints: data.completedPoints,
                        totalPoints: data.totalPoints,
                        progress: data.progress
                    }
                }));
            }
        };

        const handleSectorTrackingStopped = (data) => {
            console.log('🛑 Tracking detenido:', data);
        };

        const handleCoordinateCompleted = (data) => {
            console.log('✅ Coordenada completada via socket:', data);

            if (activeSectorTracking && data.sectorId === activeSectorTracking.sectorId) {
                const newProgress = {
                    completedPoints: data.completedPoints,
                    totalPoints: data.totalPoints,
                    progress: data.progress
                };

                console.log('📊 Actualizando progreso local:', newProgress);

                setSectorProgress(prev => ({
                    ...prev,
                    [data.sectorId]: newProgress
                }));

                setTimeout(() => {
                    console.log('🎨 Redibujando mapa desde coordinate-completed event...');
                    drawSectorProgress(newProgress);
                }, 50);

                if ('Notification' in window && Notification.permission === 'granted') {
                    new Notification('✅ Punto Completado', {
                        body: `Punto ${data.completedPoints}/${data.totalPoints} completado por ${data.completedBy?.username || 'un usuario'}`,
                        icon: '/logo192.png'
                    });
                }
            }
        };

        socketService.socket.on('sector:tracking-confirmed', handleSectorTrackingConfirmed);
        socketService.socket.on('sector:progress-updated', handleSectorProgress);
        socketService.socket.on('sector:completed', handleSectorCompleted);
        socketService.socket.on('sector:tracking-started', handleSectorTrackingStarted);
        socketService.socket.on('sector:tracking-stopped', handleSectorTrackingStopped);
        socketService.socket.on('sector:coordinate-completed', handleCoordinateCompleted);

        return () => {
            if (socketService.socket) {
                socketService.socket.off('sector:tracking-confirmed', handleSectorTrackingConfirmed);
                socketService.socket.off('sector:progress-updated', handleSectorProgress);
                socketService.socket.off('sector:completed', handleSectorCompleted);
                socketService.socket.off('sector:tracking-started', handleSectorTrackingStarted);
                socketService.socket.off('sector:tracking-stopped', handleSectorTrackingStopped);
                socketService.socket.off('sector:coordinate-completed', handleCoordinateCompleted);
            }
        };
    }, [drawSectorProgress, activeSectorTracking]);

    // 1️⃣2️⃣ Redibujar cuando cambie el tracking o progreso
    useEffect(() => {
        console.log('🔄 useEffect: activeSectorTracking o sectorProgress cambió');

        if (activeSectorTracking && sectorProgress[activeSectorTracking.sectorId]) {
            console.log('🎨 Redibujando por cambio de estado...');
            drawSectorProgress(sectorProgress[activeSectorTracking.sectorId]);
        } else if (activeSectorTracking) {
            console.log('🎨 Dibujando estado inicial...');
            drawSectorProgress({
                completedPoints: 0,
                totalPoints: activeSectorTracking.totalPoints,
                progress: 0
            });
        }
    }, [activeSectorTracking, sectorProgress, drawSectorProgress]);

    // 1️⃣3️⃣ Manejador de clic para puntos clickeables
    useEffect(() => {
        if (!mapaInstancia.current || !activeSectorTracking) return;

        const handleClick = (event) => {
            const feature = mapaInstancia.current.forEachFeatureAtPixel(
                event.pixel,
                (feature) => feature
            );

            if (feature && feature.get('isClickable')) {
                const coordinateIndex = feature.get('coordinateIndex');
                handleCoordinateClick(coordinateIndex);
            }
        };

        mapaInstancia.current.on('click', handleClick);

        return () => {
            if (mapaInstancia.current) {
                mapaInstancia.current.un('click', handleClick);
            }
        };
    }, [activeSectorTracking, handleCoordinateClick]);

    // 1️⃣4️⃣ Prevenir menú contextual en modo edición
    useEffect(() => {
        const handleContextMenu = (e) => {
            if (modoEdicion) {
                e.preventDefault()
            }
        }
        
        document.addEventListener('contextmenu', handleContextMenu)
        
        return () => {
            document.removeEventListener('contextmenu', handleContextMenu)
        }
    }, [modoEdicion])

    // 1️⃣5️⃣ Limpiar cursor al desmontar
    useEffect(() => {
        return () => {
            document.body.style.cursor = "default"
        }
    }, [])

    return (
        <div className="mapa-container">
            <div className="mapa-header">
                <h1 className="mapa-titulo">Mapa de Sectores</h1>

                {isSupervisor && (
                    <div className="supervisor-info-banner">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"></circle>
                            <line x1="12" y1="16" x2="12" y2="12"></line>
                            <line x1="12" y1="8" x2="12.01" y2="8"></line>
                        </svg>
                        <span>Visualización de sectores asignados a tu brigada ({sectors.length} sectores)</span>
                    </div>
                )}

                <div className="mapa-controls">
                    <button
                        className={`button ${isTracking ? 'button-danger' : 'button-success'}`}
                        onClick={handleToggleTracking}
                        title={isTracking ? 'Detener tracking' : 'Iniciar tracking'}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"></circle>
                            <circle cx="12" cy="12" r="3"></circle>
                        </svg>
                        {isTracking ? 'Detener GPS' : 'Activar GPS'}
                    </button>

                    <div className="search-container" ref={searchRef}>
                        <input
                            type="text"
                            value={busqueda}
                            onChange={handleBusqueda}
                            placeholder="Buscar sector..."
                            className="mapa-input"
                        />
                        {sugerencias.length > 0 && (
                            <ul className="sugerencias-lista">
                                {sugerencias.map((sector) => (
                                    <li
                                        key={sector._id}
                                        onClick={() => seleccionarSector(sector)}
                                        className="sugerencia-item"
                                    >
                                        {sector.name}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    <button className="button" onClick={centrarMapa}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="10"></circle>
                            <line x1="12" y1="8" x2="12" y2="16"></line>
                            <line x1="8" y1="12" x2="16" y2="12"></line>
                        </svg>
                        Mi ubicación
                    </button>

                    <button className="button button-secondary" onClick={resetearVista}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                            <polyline points="9 22 9 12 15 12 15 22"></polyline>
                        </svg>
                        Reiniciar vista
                    </button>

                    {isAdmin && (
                        <>
                            <button className="button" onClick={handleAddMarker}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                                    <circle cx="12" cy="10" r="3"></circle>
                                </svg>
                                Añadir Sector
                            </button>

                            <button
                                className={`button ${modoEdicion ? 'button-danger' : 'button-success'}`}
                                onClick={() => {
                                    if (modoEdicion) {
                                        setModoEdicion(false)
                                        limpiarMarcadoresTemporales()
                                        document.body.style.cursor = "default"
                                    } else {
                                        setModoEdicion(true)
                                        setSectorSeleccionado(null)
                                        if (infoOverlay) {
                                            infoOverlay.setPosition(undefined)
                                        }
                                    }
                                }}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M12 20h9"></path>
                                    <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path>
                                </svg>
                                {modoEdicion ? "Cancelar Dibujo" : "Dibujar Ruta"}
                            </button>
                        </>
                    )}

                    <button
                        className="legend-toggle-btn"
                        onClick={() => setMostrarLeyenda(!mostrarLeyenda)}
                    >
                        {mostrarLeyenda ? "✕" : "ℹ️"}
                    </button>

                    <button
                        className="button button-secondary"
                        onClick={() => setShowSectorsList(!showSectorsList)}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="8" y1="6" x2="21" y2="6"></line>
                            <line x1="8" y1="12" x2="21" y2="12"></line>
                            <line x1="8" y1="18" x2="21" y2="18"></line>
                            <line x1="3" y1="6" x2="3.01" y2="6"></line>
                            <line x1="3" y1="12" x2="3.01" y2="12"></line>
                            <line x1="3" y1="18" x2="3.01" y2="18"></line>
                        </svg>
                        {showSectorsList ? "Ocultar" : "Mostrar"} Lista
                    </button>

                    <button
                        className="button button-secondary"
                        onClick={() => setShowUsersPanel(!showUsersPanel)}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                            <circle cx="9" cy="7" r="4"></circle>
                            <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                            <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                        </svg>
                        Equipo ({Object.keys(userLocations).length})
                    </button>
                </div>
            </div>

            <div className="mapa-content">
                <div ref={mapaRef} style={{ width: "100%", height: "100%" }}></div>

                {cargando && (
                    <div className="loading-indicator">
                        <div className="spinner"></div>
                    </div>
                )}

                {/* ⚡ BANNER DE TRACKING ACTIVO */}
                {activeSectorTracking && (
                    <div className="active-tracking-banner">
                        <div className="tracking-info">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                                <circle cx="12" cy="10" r="3"></circle>
                            </svg>
                            <div>
                                <div style={{ fontSize: '14px', opacity: 0.9 }}>Recorriendo</div>
                                <div style={{ fontSize: '16px', fontWeight: 'bold' }}>
                                    {activeSectorTracking.sectorName}
                                </div>
                                <div style={{ fontSize: '12px', opacity: 0.8, marginTop: '4px' }}>
                                    Haz clic en los puntos para marcarlos
                                </div>
                            </div>
                        </div>
                        <div className="tracking-progress">
                            {sectorProgress[activeSectorTracking.sectorId] ? (
                                <>
                                    <div style={{ fontSize: '20px', fontWeight: 'bold' }}>
                                        {sectorProgress[activeSectorTracking.sectorId].completedPoints}/{activeSectorTracking.totalPoints}
                                    </div>
                                    <div style={{ fontSize: '12px', opacity: 0.8 }}>
                                        {sectorProgress[activeSectorTracking.sectorId].progress}% completado
                                    </div>
                                </>
                            ) : (
                                <div>0/{activeSectorTracking.totalPoints} puntos</div>
                            )}
                        </div>
                        <button
                            className="button button-danger button-sm"
                            onClick={handleStopSectorTracking}
                            style={{ whiteSpace: 'nowrap' }}
                        >
                            🛑 Detener
                        </button>
                    </div>
                )}

                {/* Panel Usuarios */}
                {showUsersPanel && (
                    <div className="users-online-panel">
                        <div className="users-panel-header">
                            <h3>
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                                    <circle cx="9" cy="7" r="4"></circle>
                                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                                    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                                </svg>
                                Equipo en Línea
                            </h3>
                            <div className="socket-status">
                                <span className={`status-dot ${isSocketConnected ? 'connected' : 'disconnected'}`}></span>
                                {isSocketConnected ? 'Conectado' : 'Desconectado'}
                            </div>
                        </div>
                        <div className="users-panel-body">
                            {Object.keys(userLocations).length === 0 ? (
                                <p className="no-users">No hay usuarios con ubicación activa</p>
                            ) : (
                                <ul className="users-list">
                                    {Object.values(userLocations).map((location) => (
                                        <li
                                            key={location.userId}
                                            className="user-item"
                                            onClick={() => centrarEnUsuario(location.userId)}
                                        >
                                            <div
                                                className="user-avatar"
                                                style={{ backgroundColor: getUserColor(location.userId, userLocations) }}
                                            >
                                                {location.username?.substring(0, 2).toUpperCase() || '??'}
                                            </div>
                                            <div className="user-info">
                                                <div className="user-name">{location.username}</div>
                                                <div className="user-role">{location.role}</div>
                                                <div className="user-location-time">
                                                    {new Date(location.timestamp).toLocaleTimeString('es-ES', {
                                                        hour: '2-digit',
                                                        minute: '2-digit'
                                                    })}
                                                </div>
                                            </div>
                                            <button
                                                className="btn-locate"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    centrarEnUsuario(location.userId);
                                                }}
                                                title="Centrar en mapa"
                                            >
                                                📍
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                )}

                {/* Panel Lista de Sectores */}
                {showSectorsList && (
                    <div className="sectores-programados-panel">
                        <div className="sectores-programados-header">
                            <h3>Sectores {isSupervisor ? 'Asignados' : 'Programados'}</h3>
                            <span className="badge">{sectors.length} sectores</span>
                        </div>
                        <div className="sectores-programados-body">
                            {Object.keys(sectoresPorFecha()).length === 0 ? (
                                <p className="no-sectores">
                                    {isSupervisor
                                        ? 'No tienes sectores asignados para estos días'
                                        : 'No hay sectores programados para estos días'}
                                </p>
                            ) : (
                                Object.entries(sectoresPorFecha()).map(([fecha, sectoresFecha]) => (
                                    <div key={fecha} className="fecha-grupo">
                                        <h4 className="fecha-titulo">{fecha}</h4>
                                        <ul className="sectores-lista">
                                            {sectoresFecha.map((sector) => (
                                                <li
                                                    key={sector._id}
                                                    className="sector-item"
                                                    onClick={() => seleccionarSector(sector)}
                                                >
                                                    <div className="sector-item-header">
                                                        <span className="sector-name">{sector.name}</span>
                                                        <span
                                                            className={`sector-status-badge status-${sector.status.toLowerCase()}`}
                                                            style={{ backgroundColor: getStatusColor(sector.status) }}
                                                        >
                                                            {sector.status}
                                                        </span>
                                                    </div>
                                                    {sector.description && (
                                                        <p className="sector-description">{sector.description}</p>
                                                    )}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                )}

                {/* Leyenda */}
                {mostrarLeyenda && (
                    <div className="legend">
                        <div className="legend-title">Leyenda</div>
                        <div className="legend-item">
                            <div className="legend-color" style={{ backgroundColor: COLORS.pending }}></div>
                            <span>Pendiente</span>
                        </div>
                        <div className="legend-item">
                            <div className="legend-color" style={{ backgroundColor: COLORS.inProcess }}></div>
                            <span>En proceso</span>
                        </div>
                        <div className="legend-item">
                            <div className="legend-color" style={{ backgroundColor: COLORS.success }}></div>
                            <span>Completado</span>
                        </div>
                        <div className="legend-item">
                            <div className="legend-color" style={{ backgroundColor: COLORS.userLocation }}></div>
                            <span>Mi ubicación</span>
                        </div>
                        {Object.keys(userLocations).length > 0 && (
                            <>
                                <div className="legend-divider"></div>
                                <div className="legend-subtitle">Equipo en Línea</div>
                                {Object.values(userLocations).slice(0, 5).map((location) => (
                                    <div key={location.userId} className="legend-item">
                                        <div
                                            className="legend-color"
                                            style={{ backgroundColor: getUserColor(location.userId, userLocations) }}
                                        ></div>
                                        <span>{location.username}</span>
                                    </div>
                                ))}
                                {Object.keys(userLocations).length > 5 && (
                                    <div className="legend-item">
                                        <span style={{ fontSize: '12px', color: '#666' }}>
                                            +{Object.keys(userLocations).length - 5} más
                                        </span>
                                    </div>
                                )}
                            </>
                        )}
                        {activeSectorTracking && (
                            <>
                                <div className="legend-divider"></div>
                                <div className="legend-subtitle">Tracking Activo</div>
                                <div className="legend-item">
                                    <div className="legend-color" style={{ backgroundColor: COLORS.completedPoint }}></div>
                                    <span>Puntos completados</span>
                                </div>
                                <div className="legend-item">
                                    <div className="legend-color" style={{ backgroundColor: COLORS.pendingPoint }}></div>
                                    <span>Puntos pendientes</span>
                                </div>
                            </>
                        )}
                        {modoEdicion && isAdmin && (
                            <div className="legend-item">
                                <div className="legend-color" style={{ backgroundColor: editingSector ? "#e67e22" : COLORS.newMarker }}></div>
                                <span>{editingSector ? 'Editando sector' : 'Nuevo sector'}</span>
                            </div>
                        )}
                        {isAdmin && (
                            <div className="legend-item">
                                <div className="legend-color" style={{ backgroundColor: COLORS.customMarker }}></div>
                                <span>Marcador personalizado</span>
                            </div>
                        )}
                    </div>
                )}

                {/* ⚡ MODIFICADO: Panel Modo Edición */}
                {modoEdicion && (isAdmin || editingSector) && (
                    <div className="modo-edicion-panel">
                        <div className="modo-edicion-header">
                            <h3>{editingSector ? `Editando: ${editingSector.name}` : 'Modo Dibujo de Ruta'}</h3>
                            <button
                                className="close-button"
                                onClick={() => {
                                    setModoEdicion(false)
                                    setEditingSector(null)
                                    setShowSectorForm(false)
                                    limpiarMarcadoresTemporales()
                                    document.body.style.cursor = "default"
                                }}
                            >
                                ✕
                            </button>
                        </div>
                        <div className="modo-edicion-body">
                            <div className="modo-edicion-info">
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="12" cy="12" r="10"></circle>
                                    <line x1="12" y1="16" x2="12" y2="12"></line>
                                    <line x1="12" y1="8" x2="12.01" y2="8"></line>
                                </svg>
                                <div>
                                    <p><strong>Clic izquierdo:</strong> Añadir nuevo punto</p>
                                    <p><strong>Clic derecho:</strong> Eliminar punto existente</p>
                                </div>
                            </div>

                            {coordenadasTemporal.length > 0 && (
                                <div className="puntos-counter">
                                    <strong>Puntos totales:</strong> {coordenadasTemporal.length}
                                    {editingSector && editingSector.coordinates && (
                                        <span style={{ marginLeft: '10px', fontSize: '12px', opacity: 0.8 }}>
                                            (Original: {editingSector.coordinates.length})
                                        </span>
                                    )}
                                </div>
                            )}

                            <div className="modo-edicion-actions">
                                {coordenadasTemporal.length > 0 && (
                                    <>
                                        <button
                                            className="button button-warning"
                                            onClick={deshacerUltimoPunto}
                                            title="Deshacer último punto"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <polyline points="1 4 1 10 7 10"></polyline>
                                                <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"></path>
                                            </svg>
                                            Deshacer
                                        </button>

                                        <button
                                            className="button button-secondary"
                                            onClick={limpiarMarcadoresTemporales}
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <polyline points="3 6 5 6 21 6"></polyline>
                                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                            </svg>
                                            Limpiar Todo
                                        </button>

                                        <button
                                            className="button button-success"
                                            onClick={completarDibujo}
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <polyline points="20 6 9 17 4 12"></polyline>
                                            </svg>
                                            {editingSector ? 'Guardar Cambios' : 'Completar y Guardar'}
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Panel Info Sector */}
                {sectorSeleccionado && !modoEdicion && (
                    <div className="sector-info-panel">
                        <div className={`sector-info-header status-${sectorSeleccionado.status.toLowerCase()}`}>
                            <h2 className="sector-info-title">{sectorSeleccionado.name}</h2>
                            <button
                                className="sector-info-close"
                                onClick={() => setSectorSeleccionado(null)}
                                aria-label="Cerrar"
                            >
                                ✕
                            </button>
                        </div>
                        <div className="sector-info-body">
                            <div className="sector-info-row">
                                <span className="sector-info-label">Estado:</span>
                                <span className="sector-info-value">
                                    <span className={`sector-status status-${sectorSeleccionado.status.toLowerCase()}`}>
                                        {sectorSeleccionado.status}
                                    </span>
                                </span>
                            </div>

                            {sectorSeleccionado.description && (
                                <div className="sector-info-row">
                                    <span className="sector-info-label">Descripción:</span>
                                    <span className="sector-info-value">{sectorSeleccionado.description}</span>
                                </div>
                            )}

                            {sectorSeleccionado.scheduledDate && (
                                <div className="sector-info-row">
                                    <span className="sector-info-label">Fecha Programada:</span>
                                    <span className="sector-info-value">
                                        {new Date(sectorSeleccionado.scheduledDate).toLocaleDateString('es-ES', {
                                            weekday: 'long',
                                            year: 'numeric',
                                            month: 'long',
                                            day: 'numeric'
                                        })}
                                    </span>
                                </div>
                            )}

                            {typeof sectorSeleccionado.assignmentAccepted !== "undefined" && (
                                <div className="sector-info-row">
                                    <span className="sector-info-label">Asignación Aceptada:</span>
                                    <span className="sector-info-value">
                                        {sectorSeleccionado.assignmentAccepted ? "Sí" : "No"}
                                    </span>
                                </div>
                            )}

                            <div className="sector-info-row">
                                <span className="sector-info-label">Coordenadas:</span>
                                <span className="sector-info-value">
                                    {sectorSeleccionado.coordinates && sectorSeleccionado.coordinates.length > 0
                                        ? sectorSeleccionado.coordinates.length > 1
                                            ? `${sectorSeleccionado.coordinates.length} puntos`
                                            : `${sectorSeleccionado.coordinates[0][1].toFixed(4)}, ${sectorSeleccionado.coordinates[0][0].toFixed(4)}`
                                        : "No disponibles"}
                                </span>
                            </div>

                            {sectorSeleccionado.url && (
                                <div className="sector-info-row">
                                    <span className="sector-info-label">URL:</span>
                                  <a  
                                        className="sector-info-value text-blue-600 underline"
                                        href={sectorSeleccionado.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        Ver enlace
                                    </a>
                                </div>
                            )}

                            {sectorSeleccionado.history && sectorSeleccionado.history.length > 0 && (
                                <div className="history-section">
                                    <div className="history-title">Historial</div>
                                    <ul className="history-list">
                                        {sectorSeleccionado.history.slice(0, 3).map((h, i) => (
                                            <li key={i} className="history-item">
                                                <div className="history-comment">
                                                    {h.comment || "Cambio realizado"}
                                                </div>
                                                <div className="history-date">
                                                    {new Date(h.date).toLocaleDateString('es-ES', {
                                                        day: '2-digit',
                                                        month: 'short',
                                                        year: 'numeric'
                                                    })}
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                    {sectorSeleccionado.history.length > 4 && (
                                        <div className="history-more">
                                            + {sectorSeleccionado.history.length - 4} eventos más
                                        </div>
                                    )}
                                </div>
                            )}

                            {!isAdmin && sectorSeleccionado.status !== 'Listo' && !activeSectorTracking && (
                                <div className="sector-info-actions">
                                    <button
                                        className="button button-success button-full-width"
                                        onClick={() => handleStartSectorTracking(sectorSeleccionado)}
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                                            <circle cx="12" cy="10" r="3"></circle>
                                        </svg>
                                        Iniciar Recorrido
                                    </button>
                                </div>
                            )}

                            {isAdmin && (
                                <div className="sector-info-actions">
                                    <button
                                        className="button button-primary"
                                        onClick={() => handleEditSector(sectorSeleccionado)}
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                                        </svg>
                                        Editar Sector
                                    </button>

                                    {sectorSeleccionado.status !== 'Listo' && (
                                        <button
                                            className="button button-success"
                                            onClick={async () => {
                                                if (window.confirm('Marcar este sector como completado por el administrador?')) {
                                                    try {
                                                        await apiService.put(`/api/sectores/${sectorSeleccionado._id}/complete`, {
                                                            forceComplete: true,
                                                            comment: 'Completado por administrador desde el mapa',
                                                            duration: 0
                                                        });
                                                        alert('Sector marcado como completado');
                                                        window.location.reload();
                                                    } catch (error) {
                                                        alert(error.response?.data?.message || 'Error al completar sector');
                                                    }
                                                }
                                            }}
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <polyline points="20 6 9 17 4 12"></polyline>
                                            </svg>
                                            Completar Sector
                                        </button>
                                    )}

                                    <button
                                        className="button button-danger"
                                        onClick={() => handleDeleteSector(sectorSeleccionado)}
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <polyline points="3 6 5 6 21 6"></polyline>
                                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                            <line x1="10" y1="11" x2="10" y2="17"></line>
                                            <line x1="14" y1="11" x2="14" y2="17"></line>
                                        </svg>
                                        Eliminar
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* ⚡ DIÁLOGO DE COMPLETACIÓN DE SECTOR */}
            {showCompletionDialog && completedSectorData && (
                <div className="completion-dialog-overlay">
                    <div className="completion-dialog">
                        <div className="completion-dialog-header">
                            <h2>🎉 ¡Sector Completado!</h2>
                        </div>
                        <div className="completion-dialog-body">
                            <p><strong>Sector:</strong> {completedSectorData.sectorName}</p>
                            <p><strong>Puntos completados:</strong> {completedSectorData.completedPoints}/{completedSectorData.totalPoints}</p>
                            <p><strong>Tiempo:</strong> {completedSectorData.duration} minutos</p>
                            <p>{completedSectorData.message}</p>
                        </div>
                        <div className="completion-dialog-actions">
                            <button
                                className="button button-success"
                                onClick={handleCompleteSector}
                            >
                                Sí, Marcar como Completado
                            </button>
                            <button
                                className="button button-secondary"
                                onClick={handleCancelCompletion}
                            >
                                Cancelar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ⚡ DIÁLOGO DE COMPLETACIÓN DE COORDENADA */}
            {showCoordinateDialog && selectedCoordinate && (
                <CoordinateCompletionDialog
                    isOpen={showCoordinateDialog}
                    onClose={() => setShowCoordinateDialog(false)}
                    sectorId={selectedCoordinate.sectorId}
                    sectorName={selectedCoordinate.sectorName}
                    coordinateIndex={selectedCoordinate.index}
                    totalCoordinates={selectedCoordinate.totalCoordinates}
                    onComplete={handleCoordinateComplete}
                />
            )}

            {/* Formulario de Sector */}
            {showSectorForm && isAdmin && (
                <SectorForm
                    onSubmit={editingSector ? handleUpdateSector : handleCreateSectorFromMarker}
                    sector={sectorFormData}
                    brigades={brigades}
                    circunscripciones={circunscripciones}
                    isOpen={showSectorForm}
                    onClose={handleCancelSectorForm}
                    isEditing={!!editingSector}
                />
            )}
        </div>
    )
}

export default Mapa